import { css } from '@emotion/react';
import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import InputMask from 'react-input-mask';

import { styled } from '../../../../styled';
import type { Theme } from '../../../../theme/interface/detailed';

type InputInterface = {
	className: string;
	name: string;
	initialValue?: string;
	icon?: ReactNode;
	usePhoneMask?: boolean;
	hasError: boolean;
};
const FIX_ICON = 2;
const IDENT_FOR_TEXT = 5;

const Component = ({ initialValue, icon, className, usePhoneMask, name }: InputInterface): ReactElement => {
	const ref = useRef<HTMLInputElement>(null);
	const iconRef = useRef<HTMLSpanElement>(null);
	const [padding, setPadding] = useState({ top: 0, right: 0 });
	const [inputPaddingRight, setInputPaddingRight] = useState(0);
	useEffect(() => {
		if (ref.current && !usePhoneMask) {
			ref.current.value = initialValue || '';
		}
	}, [initialValue, usePhoneMask]);

	useEffect(() => {
		if (ref.current) {
			const nodeStyle = window.getComputedStyle(ref.current);
			const top = parseInt(nodeStyle.getPropertyValue('padding-top')),
				right = parseInt(nodeStyle.getPropertyValue('padding-right'));
			setPadding({ top, right });
			setInputPaddingRight(right);
		}
		if (iconRef.current) {
			const width = iconRef.current.clientWidth + IDENT_FOR_TEXT;
			setInputPaddingRight((pr) => pr + width);
		}
	}, []);

	const inputPaddingRightCSS = useMemo(
		() =>
			css`
				body & > input {
					padding-right: ${inputPaddingRight}px;
				}
			`,
		[inputPaddingRight]
	);
	return (
		<div className={className} css={inputPaddingRight && inputPaddingRightCSS}>
			{usePhoneMask ? (
				<InputMask mask="+ 7\ (999) 999-99-99" defaultValue={initialValue}>
					{(inputProps: object): ReactElement => <input ref={ref} name={name} {...inputProps} />}
				</InputMask>
			) : (
				<input ref={ref} name={name} />
			)}
			<span
				ref={iconRef}
				css={css`
					top: ${padding.top + FIX_ICON}px;
					right: ${padding.right + FIX_ICON}px;
				`}
			>
				{icon}
			</span>
		</div>
	);
};

const Input = styled(Component)(
	({
		theme: {
			allSettings: {
				fields: { common },
			},
		},
		hasError,
	}: {
		theme: Theme;
		hasError: boolean;
	}) => css`
		position: relative;
		margin-bottom: ${common.marginBottom};

		input {
			background: ${common.background};
			border: ${hasError ? `2px solid ${common.errorColor}` : common.border};
			border-radius: ${common.borderRadius};
			box-sizing: border-box;
			font-size: ${common.fontSize};
			height: ${common.height};
			padding: ${common.padding};
			line-height: ${common.lineHeight};
			color: ${common.color};
			flex: 1;

			&:focus {
				outline: 0;
				border: ${common.onFocusBorder};
			}
		}
		& > span {
			position: absolute;
		}
	`,
	({ hasError, theme }: { hasError: boolean; theme: Theme }) =>
		`& > input{${
			hasError && {
				color: theme.allSettings.fields.common.errorColor,
			}
		}`
);

export { Input };
